/**
 * DICOM Worker Pool — Comlink edition (Upgraded)
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 *
 * Улучшения:
 *   - Синхронизация с новым API воркера (Structured Responses: success/need_more_data).
 *   - Непрерывная очередь (Concurrency Limit) вместо "жадного" Promise.all батчинга — спасает RAM.
 *   - Плавный onProgress (обновляется пофайлово).
 *   - Fallback parser теперь тоже возвращает геометрию в виде массивов чисел.
 *   - Единая функция группировки (DRY).
 */

import * as Comlink from 'comlink';
import { log } from '../utils/logger.js';
import { секундомер } from './loadStages.js';

let _pool = null;

/**
 * [10.19.23] Замер этапов внутри пула.
 *
 * ПОВОД. С машины пришло «парсинг и регистрация около 500 с на 5000 файлов», а
 * сверка сборок показала, что путь загрузки не менялся ПОБАЙТОВО с 10.19.8.
 * Значит счёт уходит не туда, где я его ищу, и разделить надо две разные вещи,
 * которые до сих пор считались одним словом «парсинг»:
 *   · ЧТЕНИЕ — file.slice(...).arrayBuffer(): байты от поставщика хранилища.
 *     На внутренней памяти микросекунды, через SAF от внешнего провайдера
 *     (SD-карта, USB-OTG, облако) — десятки миллисекунд на файл;
 *   · РАЗБОР — работа процессора в рабочем потоке.
 * И отдельно считаются повторные чтения: если заголовок не влез в первый кусок
 * (на телефоне 128 КБ), файл читается ВТОРОЙ раз. При данных с большими частными
 * тегами или встроенными эскизами так будет с каждым файлом — то есть вдвое
 * больше обращений к хранилищу при том же коде.
 */
let _счёт = секундомер();

/** Начать новый замер (зовётся из parseFilesWithWorkers). */
function _новыйЗамер() { _счёт = секундомер(); return _счёт; }

/** Снимок замера последнего парсинга — наружу, для отчёта о загрузке. */
export function замерПарсинга() { return _счёт.снимок(); }

const WORKER_TIMEOUT = 12000;
const _isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
const BUFFER_SIZES = _isMobile ? [128 * 1024, 512 * 1024] : [256 * 1024, 2 * 1024 * 1024];

// Сколько файлов одновременно читается в память и парсится
const MAX_CONCURRENCY = _isMobile ? 10 : 30;

function getPool() {
  if (_pool) return _pool;
  const cores = navigator.hardwareConcurrency || 4;
  const count = _isMobile ? Math.min(cores - 1, 3) : Math.min(cores - 1, 6);
  const workerCount = Math.max(1, count);

  const workers = [];
  for (let i = 0; i < workerCount; i++) {
    // ★ CRITICAL: URL must be inline for Vite
    const w = new Worker(
      new URL('./dicomParseWorker.js', import.meta.url),
      { type: 'module' }
    );

    w.onerror = (err) => {
      log.error(`[WorkerPool] Worker ${i} error:`, err.message || err);
    };

    workers.push({ proxy: Comlink.wrap(w), raw: w, busy: 0 });
  }

  _pool = { workers, count: workerCount };
  // [10.19.23] Число потоков и ядер идёт в отчёт: при энергосбережении и
  // перегреве Android занижает hardwareConcurrency, пул съёживается, и загрузка
  // замедляется в разы при неизменном коде. Без этой строки такое не отличить
  // от регресса в программе.
  try { _счёт.записать('потоков', workerCount); _счёт.записать('ядер', cores); } catch (e) { /* */ }
  log.debug(`[WorkerPool] Created ${workerCount} Comlink workers (ядер ${cores})`);
  return _pool;
}

function getLeastBusy() {
  const pool = getPool();
  return pool.workers.reduce((min, w) => w.busy < min.busy ? w : min, pool.workers[0]);
}

/**
 * Вызов воркера с поддержкой нового структурированного API
 */
async function sendToWorker(file, globalIdx) {
  const worker = getLeastBusy();
  worker.busy++;
  try {
    // [10.19.33] ЧТЕНИЕ ПЕРЕЕХАЛО В ВОРКЕР. Главный поток отдаёт сырой File —
    // structured clone передаёт ручку, не байты, — воркер читает каскадом сам
    // и возвращает точные тайминги. Смысл двойной: (а) ожидание поставщика
    // хранилища больше не занимает главный поток и очередь его задач;
    // (б) если поставщик сериализует обращения ПРОЦЕССА, новый секундомер
    // покажет это владельцу числом («канал насыщен»), а не догадкой.
    // Таймаут растёт с размером файла: чтение теперь внутри гонки, и большой
    // файл с медленного облака — не повод объявлять воркер мёртвым.
    const таймаутМс = WORKER_TIMEOUT + Math.min(60000, Math.round((file.size || 0) / 1024));
    try {
      const result = await Promise.race([
        worker.proxy.parseFile(file, file.name, file.size, false, { bufferSizes: BUFFER_SIZES }),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Worker timeout')), таймаутМс)),
      ]);
      if (result && result.тайминги) {
        const т = result.тайминги;
        _счёт.добавить('чтение', т.чтениеМс);
        _счёт.добавить('парсинг', т.парсингМс);
        _счёт.считать('байт', т.байт || 0);
        if (т.повторы) _счёт.считать('повторныеЧтения', т.повторы);
        _счёт.считать('чтенийВВоркере');
      }
      if (result && result.status === 'success') {
        return { meta: result.meta, fileName: file.name, file, globalIdx };
      }
      if (result && (result.status === 'error' || result.status === 'dicomdir' || result.status === 'need_more_data')) {
        return { error: result.message || `не разобран: ${file.name}`, fileName: file.name, file, globalIdx };
      }
      // Ответ незнакомой формы — воркер старого образца: буферный путь ниже.
    } catch (e) {
      if (/timeout/i.test(e.message || '')) {
        _счёт.считать('таймауты');
        return { error: e.message, fileName: file.name, file, globalIdx };
      }
      // File не доехал до воркера (среда без Blob в structured clone) — откат.
      log.debug('[WorkerPool] File-в-воркер не прошёл, буферный путь:', e.message);
    }
    return await _sendBuffered(worker, file, globalIdx);
  } finally {
    worker.busy--;
  }
}

/**
 * [10.19.33] Прежний путь: читаем на главном, шлём буфер. Оставлен ОТКАТОМ —
 * без него среда, где File в воркер не передаётся, потеряла бы загрузку
 * целиком. Замер — токенами v2: параллельные интервалы не затирают друг
 * друга, окно и занятость считаются честно.
 */
async function _sendBuffered(worker, file, globalIdx) {
  try {
    for (let attempt = 0; attempt < BUFFER_SIZES.length; attempt++) {
      const readSize = Math.min(file.size, BUFFER_SIZES[attempt]);
      const slice = file.size <= readSize ? file : file.slice(0, readSize);
      if (attempt > 0) _счёт.считать('повторныеЧтения');
      const стопЧтение = _счёт.начать('чтение');
      const buffer = await slice.arrayBuffer();
      стопЧтение();
      _счёт.считать('байт', buffer.byteLength || 0);
      const isRetry = attempt > 0;

      const стопПарсинг = _счёт.начать('парсинг');
      let result;
      try {
        result = await Promise.race([
          worker.proxy.parseFile(
            Comlink.transfer(buffer, [buffer]),
            file.name,
            file.size,
            isRetry
          ),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Worker timeout')), WORKER_TIMEOUT)
          ),
        ]);
      } finally {
        стопПарсинг();
      }

      if (result.status === 'success') {
        return { meta: result.meta, fileName: file.name, file, globalIdx };
      }

      if (result.status === 'need_more_data') {
        if (attempt < BUFFER_SIZES.length - 1) continue;
        return { error: `File truncated: ${file.name}`, fileName: file.name, file, globalIdx };
      }

      return { error: result.message, fileName: file.name, file, globalIdx };
    }

    return { error: 'Max retries exceeded', fileName: file.name, file, globalIdx };
  } catch (e) {
    if (/timeout/i.test(e.message || '')) _счёт.считать('таймауты');
    return { error: e.message || 'Unknown worker error', fileName: file.name, file, globalIdx };
  }
}

/**
 * Fallback-парсинг через dicom-parser (main thread).
 */
let _dicomParserCached = null;
async function getDicomParser() {
  if (_dicomParserCached) return _dicomParserCached;
  try { _dicomParserCached = (await import('dicom-parser')).default || (await import('dicom-parser')); } catch (e) { log.debug("[workerPool]", e.message); }
  if (!_dicomParserCached) try { const loader = await import('@cornerstonejs/dicom-image-loader'); _dicomParserCached = loader?.external?.dicomParser; } catch (e) { log.debug("[workerPool]", e.message); }
  if (!_dicomParserCached) _dicomParserCached = window.dicomParser;
  return _dicomParserCached;
}

async function fallbackParse(file, globalIdx) {
  try {
    const dp = await getDicomParser();
    if (!dp) return null;

    const headerSize = Math.min(file.size, _isMobile ? 512 * 1024 : 2 * 1024 * 1024);
    const slice = file.size <= headerSize ? file : file.slice(0, headerSize);
    const ab = await slice.arrayBuffer();
    const ds = dp.parseDicom(new Uint8Array(ab), { untilTag: 'x7fe00010' });

    const parseNumArr = (str) => typeof str === 'string' ? str.split('\\').map(parseFloat) : undefined;

    const meta = {
      sopClassUID: ds.string('x00080016') || '',
      sopInstanceUID: ds.string('x00080018') || '',
      studyDate: ds.string('x00080020') || '',
      modality: ds.string('x00080060') || '',
      manufacturer: ds.string('x00080070') || '',
      studyDesc: ds.string('x00081030') || '',
      seriesDesc: ds.string('x0008103e') || '',
      patientName: (ds.string('x00100010') || '').replace(/\^/g, ' ').trim(),
      patientId: ds.string('x00100020') || '',
      studyInstanceUID: ds.string('x0020000d') || '',
      seriesInstanceUID: ds.string('x0020000e') || '',
      instanceNumber: ds.intString('x00200013') || 0,
      numberOfFrames: ds.intString('x00280008') || 1,
      rows: ds.uint16('x00280010') || 0,
      columns: ds.uint16('x00280011') || 0,
      sliceThickness: parseFloat(ds.floatString('x00180050')) || undefined,
      
      // Приводим геометрию к массивам чисел (согласовано с новым воркером)
      imagePositionPatient: parseNumArr(ds.string('x00200032')),
      imageOrientationPatient: parseNumArr(ds.string('x00200037')),
      pixelSpacing: parseNumArr(ds.string('x00280030')),
      
      fileSize: file.size,
      _fallbackParsed: true,
    };

    if (!meta.seriesInstanceUID && !meta.studyInstanceUID) return null;
    if (!meta.seriesInstanceUID) meta.seriesInstanceUID = meta.studyInstanceUID + '.1';
    
    return { meta, fileName: file.name, file, globalIdx };
  } catch (e) {
    console.debug(`[Fallback] ${file.name}:`, e.message);
    return null;
  }
}

/**
 * Хелпер для добавления файла в карту серий (DRY)
 */
function assignToSeries(seriesMap, meta, file, globalIdx) {
  const seriesUID = meta.seriesInstanceUID || `unknown_series_${globalIdx}`;
  const studyUID  = meta.studyInstanceUID  || 'unknown_study';

  if (!seriesMap[seriesUID]) {
    seriesMap[seriesUID] = {
      seriesUID, studyUID,
      studyDate:   meta.studyDate   || '',
      studyDesc:   meta.studyDesc   || '',
      seriesDesc:  meta.seriesDesc  || '',
      patientName: meta.patientName || 'Unknown',
      patientId:   meta.patientId   || '',
      modality:    meta.modality    || 'OT',
      rows:        meta.rows        || 0,
      columns:     meta.columns     || 0,
      files: [],
    };
  }
  seriesMap[seriesUID].files.push({ file, meta, index: globalIdx });
}

export async function parseFilesWithWorkers(files, onProgress) {
  try {
    const total = files.length;
    // [10.19.23] Замер начинается заново: числа прошлой загрузки не должны
    // примешиваться к нынешней, иначе отчёт врёт в спокойную сторону.
    _новыйЗамер();
    _счёт.записать('файлов', total);
    // [10.19.42] Потоки и ядра — В КАЖДЫЙ замер, СТРОГО ПОСЛЕ обнуления:
    // первая версия этой правки легла ДО _новыйЗамер() и стиралась им —
    // пустышка, пойманная собственным выводом sed. Повод — мигание у
    // владельца: тёплый пул не перезаписывал «потоков», и вердикт
    // насыщения (потоков ≥ 2) молчал через раз при одинаковых прогонах.
    try {
      _счёт.записать('потоков', _pool?.workers?.length || 0);
      _счёт.записать('ядер', (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || 0);
    } catch (e) { /* замер не повод ронять разбор */ }
    const _пулТ0 = Date.now(); // [10.19.33] стена пула — для вердикта о насыщении канала
    const seriesMap = {};
    const errors = [];
    const failedFiles = [];
    let parsed = 0;
  
    // ── 1. Непрерывная очередь выполнения (защита от скачков RAM) ──
    const executing = new Set();
    
    for (let i = 0; i < total; i++) {
      const file = files[i];
      
      // Запускаем задачу
      const promise = sendToWorker(file, i).then(r => {
        executing.delete(promise);
        parsed++;
        
        if (r.error) {
          failedFiles.push(r);
        } else {
          assignToSeries(seriesMap, r.meta, r.file, r.globalIdx);
        }
        
        // Плавный прогресс
        if (onProgress) onProgress(parsed, total);
      });
      
      executing.add(promise);
      
      // Если достигли лимита параллелизма — ждем завершения хотя бы одной задачи
      if (executing.size >= MAX_CONCURRENCY) {
        await Promise.race(executing);
      }
    }
    
    // Ждем оставшиеся задачи
    await Promise.all(executing);
  
    // ── 2. Fallback через dicom-parser для провалившихся файлов ──
    if (failedFiles.length > 0) {
      log.debug(`[WorkerPool] Fallback: ${failedFiles.length} files via dicom-parser...`);
      
      // Для fallback используем строгую последовательность (по 1 файлу), 
      // так как он работает в main-thread и блокирует UI
      let fallbackOk = 0;
      for (const failData of failedFiles) {
        _счёт.считать('откатыВПоток');
        const r = await fallbackParse(failData.file, failData.globalIdx);
        if (r) {
          assignToSeries(seriesMap, r.meta, r.file, r.globalIdx);
          fallbackOk++;
        } else {
          errors.push(failData.fileName || 'unknown');
        }
      }
      
      if (fallbackOk > 0) log.debug(`[WorkerPool] Fallback recovered ${fallbackOk} files`);
    }
  
    // Сортировка срезов
    for (const s of Object.values(seriesMap)) {
      s.files.sort((a, b) => (a.meta.instanceNumber || 0) - (b.meta.instanceNumber || 0));
    }
  
    _счёт.записать('окноПулаМс', Date.now() - _пулТ0);
    log.debug(`[WorkerPool] Parsed ${total} files → ${Object.keys(seriesMap).length} series, ${errors.length} errors`);
    return { seriesMap, errors };
  } catch (e) { log.warn(`[workerPool] parseFilesWithWorkers failed:`, e.message); return {}; }
}

export function terminatePool() {
  if (!_pool) return;
  _pool.workers.forEach(w => {
    try { w.proxy[Comlink.releaseProxy](); } catch (e) { log.debug("[workerPool]", e.message); }
    w.raw.terminate();
  });
  _pool = null;
}

export function workersAvailable() {
  return typeof Worker !== 'undefined';
}
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Pool stats, health check, scaling
// ══════════════════════════════════════════════════════════════

/**
 * Get worker pool statistics
 * @returns {Object}
 */
export function getPoolStats() {
  if (!_pool) return { active: false, workers: 0, totalBusy: 0 };
  const busy = _pool.workers.reduce((sum, w) => sum + w.busy, 0);
  return {
    active: true,
    workers: _pool.count,
    totalBusy: busy,
    idle: _pool.count - busy,
    maxConcurrency: MAX_CONCURRENCY,
    isMobile: _isMobile,
    bufferSizes: BUFFER_SIZES,
  };
}

/**
 * Check if all workers are alive (non-terminated)
 * @returns {{alive:number, dead:number}}
 */
export function checkPoolHealth() {
  if (!_pool) return { alive: 0, dead: 0, error: 'Pool not initialized' };
  let alive = 0, dead = 0;
  for (const w of _pool.workers) {
    try { if (w.raw && !w.raw._terminated) alive++; else dead++; }
    catch { dead++; }
  }
  return { alive, dead, total: _pool.count };
}

/**
 * Restart pool (terminate + recreate)
 */
export function restartPool() {
  terminatePool();
  getPool(); // recreate
  log.debug('[WorkerPool] Restarted');
}

/**
 * Get parse statistics from first worker (Comlink proxy)
 * @returns {Promise<Object>}
 */
export async function getWorkerParseStats() {
  const pool = getPool();
  try { return await pool.workers[0].proxy.getStats(); }
  catch { return { error: 'Worker unavailable' }; }
}

/**
 * Reset parse statistics on all workers
 */
export async function resetWorkerParseStats() {
  const pool = getPool();
  for (const w of pool.workers) { try { await w.proxy.resetStats(); } catch (e) { log.debug("[workerPool]", e.message); } }
}

/**
 * Get extended DICOM tag count from worker
 * @returns {Promise<number>}
 */
export async function getWorkerTagCount() {
  const pool = getPool();
  try { return await pool.workers[0].proxy.getExtendedTagCount(); }
  catch { return -1; }
}

/**
 * Validate a single DICOM buffer via worker
 * @param {ArrayBuffer} buffer
 * @returns {Promise<Object>}
 */
export async function validateDICOMBuffer(buffer) {
  const pool = getPool();
  try {
    const result = await pool.workers[0].proxy.validateBuffer(
      Comlink.transfer(buffer, [buffer])
    );
    return result;
  } catch (e) { return { valid: false, reason: e.message }; }
}
